import type { Rng } from '../../core/rng';
import { SHIP_TEMPLATES } from './shipTemplates';
import type { PlacedShip } from './shipPlacement';
import type { SurfaceMesh } from './surfaceNet';
import { atFiringEnd, cloudShape, type PuffSize } from './gunSmoke';

/*
 * The painting shows six puffs, each of its own shape (docs/reference/ship-measure/
 * harbour-count.json), and the berths it shows them at come first in the fleet. The harbour draws
 * as many shapes, one for each of the first six salutes, from the stream of the ship that fires
 * it; every later salute, beyond the painter frame, fires one of them again in turn, at the size of
 * its own ship. Each shape costs the ships stage a surface net, and its budget holds six.
 */
export const CLOUD_SHAPES = 6;

/**
 * Each painted puff by the berth and end that fire it: its box on the museum scan against its
 * ship's hull from stern to stem (harbour-count.json, berth-sizes.json), and its crowns.
 */
export const PAINTED_PUFFS: Readonly<Record<string, PuffSize>> = {
  'berth-R3 bow': { length: 0.82, height: 0.31, billows: 3 },
  'berth-R8 stern': { length: 0.55, height: 0.35, billows: 2 },
  'berth-R8 bow': { length: 0.93, height: 0.4, billows: 3 },
  'berth-R20 bow': { length: 0.51, height: 0.28, billows: 2 },
  'berth-R26 stern': { length: 0.98, height: 0.41, billows: 3 },
  'berth-R34 bow': { length: 0.66, height: 0.34, billows: 3 },
};

function median(values: readonly number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const middle = sorted.length / 2;
  return (sorted[Math.floor(middle)]! + sorted[Math.ceil(middle) - 1]!) / 2;
}

/** The median of the painted puffs, for a salute the painting does not show. */
export const MEDIAN_PUFF: PuffSize = ((puffs) => ({
  length: median(puffs.map((p) => p.length)),
  height: median(puffs.map((p) => p.height)),
  billows: Math.round(median(puffs.map((p) => p.billows))),
}))(Object.values(PAINTED_PUFFS));

/** Every saluting ship's clouds in its own frame, one for each end that fires, by ship id. */
export function fleetClouds(
  ships: readonly PlacedShip[],
  rng: Rng
): ReadonlyMap<string, readonly SurfaceMesh[]> {
  const firing = ships.filter((ship) => ship.gunSmoke.length > 0);
  const lobes = new Map(firing.map((ship) => [ship.id, rng.fork(ship.id).fork('smoke-lobes')]));
  const salutes = firing.flatMap((ship) => ship.gunSmoke.map((end) => ({ ship, end })));
  const lengthOf = (ship: PlacedShip): number => SHIP_TEMPLATES[ship.type].length;
  const shapes = salutes.slice(0, CLOUD_SHAPES).map(({ ship, end }) => ({
    shape: cloudShape(
      lengthOf(ship),
      PAINTED_PUFFS[`${ship.id} ${end}`] ?? MEDIAN_PUFF,
      lobes.get(ship.id)!
    ),
    shipLength: lengthOf(ship),
  }));
  return new Map(
    firing.map((ship) => [
      ship.id,
      salutes.flatMap(({ ship: firer, end }, n) => {
        if (firer !== ship) return [];
        const { shape, shipLength } = shapes[n % CLOUD_SHAPES]!;
        return [atFiringEnd(shape, lengthOf(ship) / shipLength, lengthOf(ship), end)];
      }),
    ])
  );
}
